import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';

import { useLazyGetCsvQuery, useLazyGetGoogleSheetsQuery } from 'services';
import { Pagination, useTableSelection, TableSelectionV2, Table } from 'features/table';
import { Button, SelectProfileTooltip, Text } from 'shared/components/ui';
import { useFilters } from 'features/search/hooks';
import { useTechnographicsSearch } from 'features/technographics';
import { ProgAIProfile, ProgAIResults, ProgAISearchResult } from 'shared/generated-models';
import { ResultsEmpty, ResultsError, ResultsSkeleton } from 'features/search/components';
import { CandidateModal } from 'features/candidate';
import { Analytics } from 'features/analytics';
import { useIsFree } from 'features/auth';
import { TechnographicsExportToPushMenu } from 'features/integrations';
import { TechnographicsProfileRow } from 'features/technographics';
import { getFiltersText } from 'features/search';

import styles from './index.module.scss';

interface Props {
	data: ProgAISearchResult;
	isFetching: boolean;
	isError: boolean;
	setProgress: React.Dispatch<React.SetStateAction<'results' | 'export'>>;
	total: number | undefined;
}

export const TechnographicsResultsList = ({ data, isFetching, isError, setProgress, total }: Props) => {
	const [candidate, setCandidate] = useState<ProgAIResults | null>(null);
	const resultsCount = total ? total : data.results_count;

	const location = useLocation();

	const isFree = useIsFree();

	const search = useTechnographicsSearch();
	const filters = useFilters();
	const ref = useRef<HTMLDivElement>(null);

	const [getCsv] = useLazyGetCsvQuery();
	const [getGoogleSheetsUrl] = useLazyGetGoogleSheetsQuery();

	const closeCandidateModal = () => {
		setCandidate(null);
	};

	useEffect(() => {
		ref.current?.scroll({ top: 0, behavior: 'smooth' });
	}, [filters.page]);

	const {
		selectAll,
		isAllSelected,
		toggle,
		isSelected,
		someSelected,
		count,
		selectPage,
		selectCount,
		included,
		excluded,
		togglePage,
		clearSelection,
		selectedCount,
		mode
	} = useTableSelection<ProgAIProfile>({
		data: data?.results.map(row => row.profile),
		totalCount: resultsCount,
		max: resultsCount
	});

	useEffect(() => {
		if (location.state && location.state.refresh) {
			clearSelection();
		}
	}, [location.key]);

	const handleExportCsv = (isFull: boolean) => {
		Analytics.trackCsvExport(count, 'Search');
		getCsv({
			ids: included,
			filters,
			excluded_ids: excluded,
			top: selectedCount || undefined,
			is_full_profiles: isFull,
			filters_text: getFiltersText(filters)
		});
	};

	const handleExportGoogleSheets = (isFull: boolean) => {
		Analytics.trackGoogleSheetsExport(count, 'Search');
		getGoogleSheetsUrl({
			ids: included,
			filters,
			excluded_ids: excluded,
			top: selectedCount || undefined,
			is_full_profiles: isFull
		});
	};

	useEffect(() => {
		const saved = localStorage.getItem('peopleParams');
		if (saved) {
			localStorage.removeItem('peopleParams');
			try {
				getGoogleSheetsUrl(JSON.parse(saved));
			} catch (parseErr) {
				console.error('Failed to parse saved export params:', parseErr);
			}
		}
	}, []);

	if (isFetching) return <ResultsSkeleton />;

	if (isError || !data) return <ResultsError />;

	if (data.results.length === 0) return <ResultsEmpty />;

	return (
		<section className={styles.container}>
			<Table className={styles.list} ref={ref}>
				<div className={cn(styles.header, { [styles.headerBorder]: !isFree })}>
					<div className={styles.listControl}>
						<TableSelectionV2
							controls={{
								main: { togglePage, count, isAllSelected },
								extra: { selectPage, selectAll, selectCount, clearSelection }
							}}
							hint="Select"
							total={resultsCount}
						/>
						<div className={styles.infoButtons}>
							<Pagination
								page={filters.page!}
								onChange={p => search({ ...filters, page: p })}
								onPageCount={data.results.length}
								total={resultsCount}
							/>
							<SelectProfileTooltip isDisabled={!someSelected}>
								<span>
									<TechnographicsExportToPushMenu
										disabled={!someSelected}
										handleExportCsv={handleExportCsv}
										handleExportGoogleSheets={handleExportGoogleSheets}
										candidates={{
											count,
											ids: included,
											excluded_ids: excluded,
											top: selectedCount || undefined,
											filters: filters
										}}
										type="profiles"
										setProgress={setProgress}>
										<Button type="blue" disabled={!someSelected}>
											{'Export to >'}
										</Button>
									</TechnographicsExportToPushMenu>
								</span>
							</SelectProfileTooltip>
						</div>
					</div>
				</div>
				<div className={styles.card}>
					{data.results.map((candidate, idx) => (
						<TechnographicsProfileRow
							checkbox={{
								checked: isSelected({ row: candidate.profile, page: filters.page, idx }),
								onChange: () => toggle({ row: candidate.profile, page: filters.page, idx }),
								mode
							}}
							key={candidate.profile.id}
							onClick={() => setCandidate(candidate)}
							candidate={candidate}
						/>
					))}
				</div>
				<div className={styles.footer}>
					<div className={styles.footerText}>
						<Text variant="inter/13/regular" color="darker_grey">
							End of page
						</Text>
					</div>
					<div className={styles.footerPagination}>
						<Pagination
							page={filters.page}
							onChange={p => search({ ...filters, page: p })}
							onPageCount={data.results.length}
							total={resultsCount}
						/>
					</div>
				</div>
			</Table>
			{candidate && (
				<CandidateModal
					candidates={data.results}
					setCandidate={setCandidate}
					candidate={candidate}
					onClose={closeCandidateModal}
					total={resultsCount}
					page={filters.page}
					onChange={p => search({ ...filters, page: p })}
				/>
			)}
		</section>
	);
};
